import React, { useState, useMemo } from 'react';
import { Button, Icon, Input, useGraphQLQuery } from '@tviklax/ui-components';
import { gql } from '@apollo/client';
import { PaymentHistoryItem } from './types';
import {
  HistoryContainer,
  PageHeader,
  HeaderContent,
  Title,
  Subtitle,
  FiltersBar,
  Select,
  TransactionCard,
  CardHeader,
  TransactionInfo,
  TransactionId,
  DateTime,
  Amount,
  CardBody,
  InfoGroup,
  InfoLabel,
  InfoValue,
  BadgeGroup,
  StatusBadge,
  TypeBadge,
} from './styles';
import { formatCurrency } from '@tviklax/ui-components';

const PAYMENT_HISTORY_QUERY = gql`
  query PaymentHistoryData($limit: Int = 500, $offset: Int = 0) {
    orderPayments(limit: $limit, offset: $offset) {
      id
      orderId
      amount
      currency
      paymentStatus
      paymentMethod
      transactionReference
      createdAt
    }
    orders(limit: $limit, offset: $offset) {
      id
      orderNo
    }
  }
`;

const getPaymentStatusLabel = (status: number | undefined): 'Completed' | 'Processing' | 'Failed' => {
  if (status === 1) return 'Completed';
  if (status === 2) return 'Failed';
  return 'Processing';
};

const getPaymentTypeLabel = (amount: number): string => {
  if (amount >= 5000 * 0.5) return 'Full Payment';
  if (amount > 0) return 'Partial Payment';
  return 'Additional Payment';
};

export const PaymentHistory: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const { data: paymentData } = useGraphQLQuery<{
    orderPayments: Array<{ id: number; orderId: number; amount: number; currency: string; paymentStatus: number; paymentMethod: string; transactionReference: string; createdAt: string }>;
    orders: Array<{ id: number; orderNo: string }>;
  }>(PAYMENT_HISTORY_QUERY, {
    variables: { limit: 500, offset: 0 },
    showErrorToast: false,
  });

  const paymentHistory = useMemo(() => {
    const payments = paymentData?.orderPayments || [];
    const orders = paymentData?.orders || [];

    if (!payments.length) {
      return [];
    }

    const orderMap = new Map(orders.map(o => [o.id, o.orderNo]));

    return payments.map(p => ({
      id: `TXN-${p.id}`,
      orderId: orderMap.get(p.orderId) || `ORD-${p.orderId}`,
      customer: `Order ${p.orderId}`,
      amount: p.amount,
      type: getPaymentTypeLabel(p.amount),
      method: p.paymentMethod || 'Bank Transfer',
      status: getPaymentStatusLabel(p.paymentStatus),
      date: new Date(p.createdAt).toLocaleDateString(),
      time: new Date(p.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      reference: p.transactionReference || `REF-${p.id}`,
    })) as PaymentHistoryItem[];
  }, [paymentData]);

  return (
    <HistoryContainer>
      <PageHeader initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <HeaderContent>
          <Title>Payment History</Title>
          <Subtitle>View all transaction records and payment history</Subtitle>
        </HeaderContent>
        <Button>
          <Icon name="Download" size={16} />
          Export Data
        </Button>
      </PageHeader>

      <FiltersBar initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Input
          type="text"
          placeholder="Search by transaction ID, order ID, or customer..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          startAdornment={<Icon name="Search" size={16} />}
        />
        <Select value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)}>
          <option value="all">All Time</option>
          <option value="today">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="year">This Year</option>
        </Select>
      </FiltersBar>

      {paymentHistory.length === 0 && (
        <TransactionCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <CardBody>
            <InfoGroup>
              <InfoLabel>Transactions</InfoLabel>
              <InfoValue>No payment records found in database.</InfoValue>
            </InfoGroup>
          </CardBody>
        </TransactionCard>
      )}

      {paymentHistory.map((transaction, index) => (
        <TransactionCard
          key={transaction.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 + index * 0.1 }}
        >
          <CardHeader>
            <TransactionInfo>
              <TransactionId>{transaction.id}</TransactionId>
              <DateTime>
                {new Date(transaction.date).toLocaleDateString()} at {transaction.time}
              </DateTime>
            </TransactionInfo>
            <Amount>{formatCurrency(transaction.amount)}</Amount>
          </CardHeader>

          <CardBody>
            <InfoGroup>
              <InfoLabel>Order ID</InfoLabel>
              <InfoValue>{transaction.orderId}</InfoValue>
            </InfoGroup>
            <InfoGroup>
              <InfoLabel>Customer</InfoLabel>
              <InfoValue>{transaction.customer}</InfoValue>
            </InfoGroup>
            <InfoGroup>
              <InfoLabel>Payment Method</InfoLabel>
              <InfoValue>{transaction.method}</InfoValue>
            </InfoGroup>
            <InfoGroup>
              <InfoLabel>Reference</InfoLabel>
              <InfoValue>{transaction.reference}</InfoValue>
            </InfoGroup>
            <InfoGroup>
              <InfoLabel>Type & Status</InfoLabel>
              <BadgeGroup>
                <TypeBadge $type={transaction.type}>{transaction.type}</TypeBadge>
                <StatusBadge $status={transaction.status}>{transaction.status}</StatusBadge>
              </BadgeGroup>
            </InfoGroup>
          </CardBody>

          <Button variant="outline" size="sm">
            <Icon name="Download" size={14} />
            Download Receipt
          </Button>
        </TransactionCard>
      ))}
    </HistoryContainer>
  );
};
